import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import p1 from '../assets/food/q.mp4';

// Основной контейнер
const FoodContainer = styled(motion.div)`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  overflow: hidden;
  background-color: #000;
`;

// Видео контейнер на весь экран
const VideoBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -2;
`;

// Стиль для видео на весь экран
const BackgroundVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

// Тёмный оверлей поверх видео (чёрный фон без градиента)
const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35); // Просто черный фон с высокой непрозрачностью
  z-index: -1;
`;

// Контейнер для текста и спиннера
const ContentContainer = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  z-index: 10;
`;

// Обновленный стиль заголовка с градиентным свечением как в SplashScreen
const Text = styled(motion.h1)`
  font-size: 3.5rem;
  text-align: center;
  margin: 0;
  position: relative;
  text-transform: none;
  font-family: 'Black Han Sans', sans-serif;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.1;
  
  /* Базовый белый цвет */
  color: white;
  
  .white-text {
    color: white;
  }
  
  .gradient-text {
    /* Яркий градиент в фиолетово-розовых тонах - точно как в SplashScreen */
    background: linear-gradient(
      120deg,
      #ffffff 0%,
      #ff6ad5 25%,
      #c659ff 50%,
      #ff36be 75%,
      #ffffff 100%
    );
    background-size: 400% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    
    /* Плавная анимация переливания градиента */
    animation: gradientFlow 6s ease infinite;
  }
  
  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

// Контейнер для лапы с более ярким свечением
const IconContainer = styled(motion.div)`
  position: relative;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 120px;
  height: 120px;
`;

// Пульсирующий эффект вокруг иконки (основной круг) с усиленным свечением
const PulseEffect = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(146, 0, 179, 0.6);
  z-index: -1;
  filter: blur(10px);
`;

// Вторичный пульсирующий эффект с неоновым свечением
const SecondaryPulse = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(255, 54, 190, 0.7);
  z-index: -2;
  box-shadow: 
    0 0 10px #ff36be,
    0 0 20px rgba(255, 54, 190, 0.5),
    inset 0 0 15px rgba(255, 54, 190, 0.5);
`;

// Третий световой эффект с более ярким градиентом
const GlowEffect = styled(motion.div)`
  position: absolute;
  width: 160%;
  height: 160%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(165, 0, 203, 0.25) 0%, rgba(0, 0, 0, 0) 70%);
  z-index: -3;
  filter: blur(5px);
`;

// Стилизованная иконка без белого свечения
const Icon = styled.img`
  width: 70px;
  height: 70px;
  object-fit: contain;
  z-index: 10;
//   filter: drop-shadow(0 0 8px rgba(198, 89, 255, 0.1));
`;

const MainTitle = styled(Text)`
  font-size: 3.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled(Text)`
  font-size: 1.4rem;
  margin-top: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const KnowsText = styled(Text)`
  font-size: 1.1rem;
  margin-top: 5px;
  opacity: 0.9;
`;

// Компонент Food
const Food = () => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [hideVideo, setHideVideo] = useState(false);
  const navigate = useNavigate();

  // Добавляем useEffect для управления скоростью видео
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.5; // Ускоряем видео в 1.5 раза
    }
  }, []);

  const handleVideoEnded = () => {
    setHideVideo(true);
    
    // Уменьшаем задержку перед показом лапки
    setTimeout(() => {
      setShowContent(true);
    }, 200); // было 400

    setTimeout(() => {
      navigate('/home');
    }, 2000); // было 2800
  };

  return (
    <AnimatePresence>
      <FoodContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }} // было 0.8
      >
        {!hideVideo && (
          <VideoBackground
            initial={{ opacity: 0 }}
            animate={{ opacity: videoLoaded ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <BackgroundVideo
              ref={videoRef}
              playsInline
              muted
              autoPlay
              onLoadedData={() => setVideoLoaded(true)}
              onEnded={handleVideoEnded}
            >
              <source src={p1} type="video/mp4" />
              Ваш браузер не поддерживает видео тег.
            </BackgroundVideo>
          </VideoBackground>
        )}
        
        <Overlay 
          initial={{ opacity: 0 }}
          animate={{ opacity: hideVideo ? 1 : 0.85 }}
          transition={{ duration: 0.5 }} // было 0.8
        />
        
        {showContent && (
          <ContentContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }} // было 1
          >
            <IconContainer
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                delay: 0.1, // было 0.4
                duration: 0.5, // было 0.8
                type: "spring", 
                stiffness: 200 
              }}
            >
              <GlowEffect 
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{
                  duration: 2, // было 3
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
              <SecondaryPulse 
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.7, 0.4, 0.7]
                }}
                transition={{
                  duration: 2, // было 2.5
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
              <PulseEffect 
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [0.7, 0.5, 0.7]
                }}
                transition={{
                  duration: 1.5, // было 2
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
              <Icon 
                src={require('../assets/lapa.png')} 
                alt="Paw icon"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  delay: 0.1, // было 0.5
                  duration: 0.3 
                }}
                as={motion.img}
              />
            </IconContainer>
          </ContentContainer>
        )}
      </FoodContainer>
    </AnimatePresence>
  );
};

export default Food;